import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { CartApiAdapter, ReceiveApiAdapter } from '../adapters';
import {
  IdentifiedOrder,
  IncidenceType,
  OrderLine,
  ReceiveFlowState,
  ReceiveOrderLineRequest,
  RejectionReason,
} from '../models';

// Verifica si una línea del pedido tiene todas sus unidades escaneadas (incluye expansión de packs)
const isOrderLineComplete = (orderId: number, line: OrderLine, scannedQuantities: Record<string, number>): boolean => {
  if (line.pack?.lines) {
    const packQty = line.qty;

    return line.pack.lines.every((packLine) => {
      const productId = packLine.product_id;
      if (!productId) return true;

      const scannedKey = `${orderId}:${line.id}:${productId}`;
      const scannedQty = scannedQuantities[scannedKey] || 0;
      const expectedQty = packQty * packLine.qty;

      return scannedQty >= expectedQty;
    });
  }

  if (line.product_id) {
    const scannedKey = `${orderId}:${line.id}`;
    const scannedQty = scannedQuantities[scannedKey] || 0;

    return scannedQty >= line.qty;
  }

  return true;
};

export const useReceiveFlow = (receptionId: number) => {
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const receiveAdapter = useMemo(() => new ReceiveApiAdapter(httpClient), [httpClient]);
  const cartAdapter = useMemo(() => new CartApiAdapter(httpClient), [httpClient]);
  const queryClient = useQueryClient();

  // Estado del flujo
  const [state, setState] = useState<ReceiveFlowState>({
    currentStep: 'cart',
    validatedCart: null,
    selectedCourierId: null,
    identifiedOrder: null,
    isProcessing: false,
    error: null,
  });

  // Lista de pedidos identificados en la sesión
  const [identifiedOrders, setIdentifiedOrders] = useState<IdentifiedOrder[]>([]);

  // Cantidades escaneadas: `${orderId}:${lineId}` (producto) o `${orderId}:${lineId}:${productId}` (pack)
  const [scannedQuantities, setScannedQuantities] = useState<Record<string, number>>({});

  // Lotes seleccionados por product_id
  const [selectedBatchIds, setSelectedBatchIds] = useState<Record<number, number | null>>({});

  // Estado de recepción en curso
  const [isReceiving, setIsReceiving] = useState(false);

  // Query para obtener la recepción
  const {
    data: receptionData,
    isLoading: isLoadingReception,
    error: receptionError,
    refetch: refetchReception,
  } = useQuery({
    queryKey: ['reused-reception', receptionId],
    queryFn: () => receiveAdapter.getReception(receptionId),
    enabled: receptionId > 0,
  });

  // Paso 1: Validar carrito por código escaneado
  const validateCart = useCallback(
    async (code: string): Promise<{ success: boolean; message: string }> => {
      setState((prev) => ({ ...prev, isProcessing: true, error: null }));

      try {
        const cart = await cartAdapter.searchByCode(code.trim());

        if (!cart) {
          setState((prev) => ({
            ...prev,
            isProcessing: false,
            error: `No se encontró ningún carrito con el código "${code}"`,
          }));
          return {
            success: false,
            message: `Carrito con código "${code}" no encontrado`,
          };
        }

        // Validar que el carrito no tenga productos de otra recepción o sin asignar (null)
        const invalidItems =
          cart.stock_location_carts?.filter(
            (item) => item.returned_reception_id === null || item.returned_reception_id !== receptionId,
          ) || [];

        if (invalidItems.length > 0) {
          const hasNullReception = invalidItems.some((item) => item.returned_reception_id === null);
          const errorMsg = hasNullReception
            ? `El carrito "${cart.code}" tiene productos sin asignar a esta recepción`
            : `El carrito "${cart.code}" tiene productos asignados a otra recepción`;

          setState((prev) => ({
            ...prev,
            isProcessing: false,
            error: errorMsg,
          }));
          return {
            success: false,
            message: errorMsg,
          };
        }

        // Carrito válido - avanzar al paso 2 (identificar pedido)
        setState((prev) => ({
          ...prev,
          isProcessing: false,
          validatedCart: cart,
          currentStep: 'identify',
          error: null,
        }));

        return {
          success: true,
          message: `Carrito "${cart.code}" validado correctamente`,
        };
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          'Error al validar el carrito';

        setState((prev) => ({
          ...prev,
          isProcessing: false,
          error: errorMessage,
        }));

        return {
          success: false,
          message: errorMessage,
        };
      }
    },
    [cartAdapter, receptionId],
  );

  // Seleccionar courier (para el paso 2)
  const selectCourier = useCallback((courierId: number | null) => {
    setState((prev) => ({
      ...prev,
      selectedCourierId: courierId,
      error: null,
    }));
  }, []);

  // Paso 2: Identificar pedido por código de barras + courier
  const identifyOrder = useCallback(
    async (barcode: string): Promise<{ success: boolean; message: string }> => {
      if (!state.selectedCourierId) {
        return {
          success: false,
          message: 'Debes seleccionar un courier primero',
        };
      }

      setState((prev) => ({ ...prev, isProcessing: true, error: null }));

      try {
        const order = await receiveAdapter.identifyOrder({
          barcode: barcode.trim(),
          courier_id: state.selectedCourierId,
        });

        // Verificar si el pedido ya fue escaneado
        const alreadyScanned = identifiedOrders.some((o) => o.id === order.id);
        if (alreadyScanned) {
          setState((prev) => ({
            ...prev,
            isProcessing: false,
            error: `El pedido ${order.ref} ya fue escaneado anteriormente`,
          }));
          return {
            success: false,
            message: `El pedido ${order.ref} ya fue escaneado anteriormente`,
          };
        }

        // Agregar a la lista de pedidos identificados
        setIdentifiedOrders((prev) => [order, ...prev]);

        // Pasar al paso 3 (escanear productos)
        setState((prev) => ({
          ...prev,
          isProcessing: false,
          identifiedOrder: order,
          currentStep: 'lines',
        }));

        return {
          success: true,
          message: `Pedido ${order.ref} identificado correctamente`,
        };
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          'Error al identificar el pedido';

        setState((prev) => ({
          ...prev,
          isProcessing: false,
          error: errorMessage,
        }));

        return {
          success: false,
          message: errorMessage,
        };
      }
    },
    [state.selectedCourierId, identifiedOrders, receiveAdapter],
  );

  // Paso 3: Escanear producto del pedido
  const scanProduct = useCallback(
    async (code: string): Promise<{ success: boolean; message: string }> => {
      const order = state.identifiedOrder;
      if (!order) {
        return {
          success: false,
          message: 'No hay pedido seleccionado',
        };
      }

      const trimmedCode = code.trim().toLowerCase();

      // Estructura para almacenar coincidencias: { line, packLine?, productId, expectedQty }
      type Match = {
        line: (typeof order.lines)[0];
        packLine?: { product_id: number; qty: number; product?: any };
        productId: number;
        expectedQty: number;
        productName: string;
        isPack: boolean;
      };

      const matches: Match[] = [];

      // Buscar en todas las líneas del pedido
      order.lines.forEach((line) => {
        // Si la línea tiene un pack, buscar dentro del pack
        if (line.pack && line.pack.lines) {
          line.pack.lines.forEach((packLine) => {
            const productCode = (packLine.product?.code || '').toLowerCase();
            const productEan = (packLine.product?.ean || '').toLowerCase();

            if (productCode === trimmedCode || productEan === trimmedCode) {
              const productId = packLine.product_id;
              if (productId) {
                // Cantidad esperada: cantidad del pack * cantidad del producto en el pack
                const expectedQty = line.qty * packLine.qty;
                matches.push({
                  line,
                  packLine,
                  productId,
                  expectedQty,
                  productName: packLine.product?.name || `Producto ID: ${productId}`,
                  isPack: true,
                });
              }
            }
          });
        } else if (line.product_id && line.product) {
          // Producto individual (no pack)
          const productCode = (line.product.code || '').toLowerCase();
          const productEan = (line.product.ean || '').toLowerCase();

          if (productCode === trimmedCode || productEan === trimmedCode) {
            matches.push({
              line,
              productId: line.product_id,
              expectedQty: line.qty,
              productName: line.product.name || line.name || `Producto ID: ${line.product_id}`,
              isPack: false,
            });
          }
        }
      });

      if (matches.length === 0) {
        return {
          success: false,
          message: `Producto con código "${code}" no encontrado en este pedido`,
        };
      }

      // Buscar la primera coincidencia que tenga unidades pendientes
      let matchToIncrement: Match | null = null;
      for (const match of matches) {
        let lineKey: string;
        let currentQty: number;

        if (match.isPack) {
          // Para packs: clave incluye el productId del producto dentro del pack
          lineKey = `${order.id}:${match.line.id}:${match.productId}`;
          currentQty = scannedQuantities[lineKey] || 0;
        } else {
          // Para productos individuales: clave solo con orderId y lineId
          lineKey = `${order.id}:${match.line.id}`;
          currentQty = scannedQuantities[lineKey] || 0;
        }

        if (currentQty < match.expectedQty) {
          matchToIncrement = match;
          break;
        }
      }

      // Si todas las coincidencias están completas
      if (!matchToIncrement) {
        const productName = matches[0].productName;
        return {
          success: false,
          message: `El producto "${productName}" ya está completo`,
        };
      }

      // Incrementar cantidad escaneada
      let lineKey: string;
      if (matchToIncrement.isPack) {
        lineKey = `${order.id}:${matchToIncrement.line.id}:${matchToIncrement.productId}`;
      } else {
        lineKey = `${order.id}:${matchToIncrement.line.id}`;
      }

      const currentQty = scannedQuantities[lineKey] || 0;
      const newQty = currentQty + 1;

      setScannedQuantities((prev) => ({
        ...prev,
        [lineKey]: newQty,
      }));

      const productName = matchToIncrement.productName;

      // Calcular totales para el mensaje (agrupando por productId)
      const productId = matchToIncrement.productId;
      const productMatches = matches.filter((m) => m.productId === productId);

      const totalExpected = productMatches.reduce((sum, m) => sum + m.expectedQty, 0);
      const totalScanned =
        productMatches.reduce((sum, m) => {
          let key: string;
          if (m.isPack) {
            key = `${order.id}:${m.line.id}:${m.productId}`;
          } else {
            key = `${order.id}:${m.line.id}`;
          }
          return sum + (scannedQuantities[key] || 0);
        }, 0) + 1; // +1 por el que acabamos de escanear

      if (totalScanned >= totalExpected) {
        return {
          success: true,
          message: `✓ ${productName} completado (${totalScanned}/${totalExpected})`,
        };
      }

      return {
        success: true,
        message: `${productName}: ${totalScanned}/${totalExpected}`,
      };
    },
    [state.identifiedOrder, scannedQuantities],
  );

  // Recepcionar pedido (con datos del modal si hay productos faltantes)
  const receiveOrder = useCallback(
    async (modalData?: {
      hasCourierIncidence: boolean;
      incidenceType: IncidenceType | null;
      rejectionReasons: Record<string | number, RejectionReason>;
      rejectedQuantities: Record<string | number, number>;
    }): Promise<{ success: boolean; message: string }> => {
      const order = state.identifiedOrder;
      const cart = state.validatedCart;

      if (!order || !cart) {
        return {
          success: false,
          message: 'No hay pedido o carrito seleccionado',
        };
      }

      setIsReceiving(true);
      setState((prev) => ({ ...prev, error: null }));

      try {
        // Si hay incidencia de courier, NO enviar líneas (el backend las maneja automáticamente)
        const lines: ReceiveOrderLineRequest[] = [];

        if (!modalData?.hasCourierIncidence) {
          // Solo construir líneas si NO hay incidencia de courier
          order.lines.forEach((line) => {
            // Si la línea tiene un pack, expandir a productos individuales
            if (line.pack && line.pack.lines) {
              const packQty = line.qty; // Cantidad de packs

              line.pack.lines.forEach((packLine) => {
                const productId = packLine.product_id;
                if (!productId) return;

                // Clave para escaneos de productos dentro de packs
                const packLineKey = `${order.id}:${line.id}:${productId}`;
                const scannedQty = scannedQuantities[packLineKey] || 0;

                // Cantidad esperada: cantidad del pack * cantidad del producto en el pack
                const expectedQty = packQty * packLine.qty;
                const isComplete = scannedQty >= expectedQty;

                if (isComplete) {
                  // Producto completamente escaneado - aceptado
                  lines.push({
                    product_id: productId,
                    quantity: scannedQty,
                    status: 'ACEPTADO' as const,
                    rejection_reason: null,
                    rejected_imgs: null,
                    stock_location_order_id: null,
                    ...(selectedBatchIds[productId] && { batch_id: selectedBatchIds[productId]! }),
                  });
                } else {
                  // Producto con unidades faltantes - enviar dos líneas si hay algo escaneado

                  // Línea aceptada (si se escaneó algo)
                  if (scannedQty > 0) {
                    lines.push({
                      product_id: productId,
                      quantity: scannedQty,
                      status: 'ACEPTADO' as const,
                      rejection_reason: null,
                      rejected_imgs: null,
                      stock_location_order_id: null,
                      ...(selectedBatchIds[productId] && { batch_id: selectedBatchIds[productId]! }),
                    });
                  }

                  // Línea rechazada (con la cantidad rechazada del modal)
                  // Para packs, usar la clave combinada para obtener rechazos
                  const rejectionKey = `${line.id}:${productId}`;
                  const rejectionReason = modalData?.rejectionReasons[rejectionKey] || RejectionReason.OTRO;
                  const rejectedQty = modalData?.rejectedQuantities[rejectionKey] || expectedQty - scannedQty;

                  if (rejectedQty > 0) {
                    lines.push({
                      product_id: productId,
                      quantity: rejectedQty,
                      status: 'RECHAZADO' as const,
                      rejection_reason: rejectionReason,
                      rejected_imgs: null,
                      stock_location_order_id: null,
                    });
                  }
                }
              });
            } else if (line.product_id) {
              // Producto individual (no pack)
              const lineKey = `${order.id}:${line.id}`;
              const scannedQty = scannedQuantities[lineKey] || 0;
              const isComplete = scannedQty >= line.qty;

              if (isComplete) {
                // Producto completamente escaneado - aceptado
                lines.push({
                  product_id: line.product_id,
                  quantity: scannedQty,
                  status: 'ACEPTADO' as const,
                  rejection_reason: null,
                  rejected_imgs: null,
                  stock_location_order_id: null,
                  ...(selectedBatchIds[line.product_id] && { batch_id: selectedBatchIds[line.product_id]! }),
                });
              } else {
                // Producto con unidades faltantes - enviar dos líneas si hay algo escaneado

                // Línea aceptada (si se escaneó algo)
                if (scannedQty > 0) {
                  lines.push({
                    product_id: line.product_id,
                    quantity: scannedQty,
                    status: 'ACEPTADO' as const,
                    rejection_reason: null,
                    rejected_imgs: null,
                    stock_location_order_id: null,
                    ...(selectedBatchIds[line.product_id] && { batch_id: selectedBatchIds[line.product_id]! }),
                  });
                }

                // Línea rechazada (con la cantidad rechazada del modal)
                const rejectionReason = modalData?.rejectionReasons[line.id] || RejectionReason.OTRO;
                const rejectedQty = modalData?.rejectedQuantities[line.id] || line.qty - scannedQty;

                if (rejectedQty > 0) {
                  lines.push({
                    product_id: line.product_id,
                    quantity: rejectedQty,
                    status: 'RECHAZADO' as const,
                    rejection_reason: rejectionReason,
                    rejected_imgs: null,
                    stock_location_order_id: null,
                  });
                }
              }
            }
          });
        }

        await receiveAdapter.receiveOrder(receptionId, {
          order_id: order.id,
          cart_id: cart.id,
          lines,
          has_courier_incidence: modalData?.hasCourierIncidence ?? false,
          incidence_type: modalData?.incidenceType ?? null,
        });

        // Limpiar estado después de recepcionar
        setScannedQuantities({});
        setState((prev) => ({
          ...prev,
          identifiedOrder: null,
          currentStep: 'identify',
        }));

        // Forzar sincronización de datos clave para evitar estados visuales desfasados.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['received-lines', receptionId] }),
          queryClient.invalidateQueries({ queryKey: ['processed-orders', receptionId] }),
          queryClient.invalidateQueries({ queryKey: ['location-orders', receptionId] }),
          queryClient.invalidateQueries({ queryKey: ['reused-reception', receptionId] }),
          queryClient.invalidateQueries({ queryKey: ['receptionCarts', receptionId] }),
        ]);

        await Promise.all([
          refetchReception(),
          queryClient.refetchQueries({ queryKey: ['received-lines', receptionId], type: 'active' }),
          queryClient.refetchQueries({ queryKey: ['processed-orders', receptionId], type: 'active' }),
          queryClient.refetchQueries({ queryKey: ['location-orders', receptionId], type: 'active' }),
          queryClient.refetchQueries({ queryKey: ['reused-reception', receptionId], type: 'active' }),
          queryClient.refetchQueries({ queryKey: ['receptionCarts', receptionId], type: 'active' }),
        ]);

        // Actualizar el carrito para reflejar los cambios en stock_location_carts
        if (cart.code) {
          try {
            const updatedCart = await cartAdapter.searchByCode(cart.code);
            if (updatedCart) {
              setState((prev) => ({
                ...prev,
                validatedCart: updatedCart,
              }));
            }
          } catch (error) {
            console.error('Error al actualizar el carrito:', error);
          }
        }

        setIsReceiving(false);

        return {
          success: true,
          message: `Pedido ${order.ref} recepcionado correctamente`,
        };
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          'Error al recepcionar el pedido';

        setState((prev) => ({
          ...prev,
          error: errorMessage,
        }));

        setIsReceiving(false);

        return {
          success: false,
          message: errorMessage,
        };
      }
    },
    [
      state.identifiedOrder,
      state.validatedCart,
      scannedQuantities,
      receptionId,
      receiveAdapter,
      refetchReception,
      queryClient,
      cartAdapter,
    ],
  );

  // Procesar escaneo según el paso actual
  const processScan = useCallback(
    async (code: string): Promise<{ success: boolean; message: string }> => {
      if (state.currentStep === 'cart') {
        return validateCart(code);
      }
      if (state.currentStep === 'identify') {
        return identifyOrder(code);
      }
      if (state.currentStep === 'lines') {
        return scanProduct(code);
      }
      return { success: false, message: 'Paso no válido para escaneo' };
    },
    [state.currentStep, validateCart, identifyOrder, scanProduct],
  );

  // Limpiar error
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  // Limpiar pedido identificado y volver al paso 2
  const clearIdentifiedOrder = useCallback(() => {
    const currentOrderId = state.identifiedOrder?.id;

    setState((prev) => ({
      ...prev,
      identifiedOrder: null,
      currentStep: 'identify',
    }));

    // Eliminar el pedido de la lista de identificados para poder volverlo a escanear
    if (currentOrderId) {
      setIdentifiedOrders((prev) => prev.filter((o) => o.id !== currentOrderId));
    }

    // Limpiar cantidades escaneadas del pedido actual
    setScannedQuantities({});
  }, [state.identifiedOrder]);

  // Volver al paso anterior
  const goBack = useCallback(() => {
    const currentOrderId = state.identifiedOrder?.id;

    setState((prev) => {
      if (prev.currentStep === 'lines') {
        return { ...prev, currentStep: 'identify', identifiedOrder: null };
      }
      if (prev.currentStep === 'identify') {
        return { ...prev, currentStep: 'cart', validatedCart: null, selectedCourierId: null };
      }
      return prev;
    });

    // Limpiar cantidades y eliminar pedido de la lista si volvemos del paso 3
    if (state.currentStep === 'lines') {
      setScannedQuantities({});

      // Eliminar el pedido de la lista de identificados para poder volverlo a escanear
      if (currentOrderId) {
        setIdentifiedOrders((prev) => prev.filter((o) => o.id !== currentOrderId));
      }
    }
  }, [state.currentStep, state.identifiedOrder]);

  // Reiniciar todo el flujo
  const resetFlow = useCallback(() => {
    setState({
      currentStep: 'cart',
      validatedCart: null,
      selectedCourierId: null,
      identifiedOrder: null,
      isProcessing: false,
      error: null,
    });
    setIdentifiedOrders([]);
    setScannedQuantities({});
  }, []);

  // Verificar si todos los productos del pedido están completos
  const isOrderComplete = useCallback(() => {
    const order = state.identifiedOrder;
    if (!order || !order.lines) return false;

    return order.lines.every((line) => isOrderLineComplete(order.id, line, scannedQuantities));
  }, [state.identifiedOrder, scannedQuantities]);

  // Verificar si hay productos faltantes
  const hasMissingProducts = useCallback(() => {
    const order = state.identifiedOrder;
    if (!order || !order.lines) return false;

    return order.lines.some((line) => !isOrderLineComplete(order.id, line, scannedQuantities));
  }, [state.identifiedOrder, scannedQuantities]);

  const handleBatchChange = (productId: number, batchId: number | null) => {
    setSelectedBatchIds((prev) => ({ ...prev, [productId]: batchId }));
  };

  return {
    // Datos de la recepción
    reception: receptionData?.data,
    isLoadingReception,
    receptionError: receptionError?.message ?? null,
    refetchReception,

    // Estado del flujo
    currentStep: state.currentStep,
    validatedCart: state.validatedCart,
    selectedCourierId: state.selectedCourierId,
    identifiedOrder: state.identifiedOrder,
    identifiedOrders,
    scannedQuantities,
    selectedBatchIds,
    isProcessing: state.isProcessing,
    isReceiving,
    error: state.error,
    isOrderComplete: isOrderComplete(),
    hasMissingProducts: hasMissingProducts(),

    // Acciones
    validateCart,
    selectCourier,
    identifyOrder,
    scanProduct,
    receiveOrder,
    processScan,
    clearError,
    clearIdentifiedOrder,
    goBack,
    resetFlow,
    handleBatchChange,
  };
};
